import React from 'react';
import {
	Box,
	Flex,
	HStack,
	Text,
	Button,
	Badge,
	Avatar,
	Tooltip,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Spinner,
	useDisclosure,
} from '@chakra-ui/react';
import useAppToast from 'components/toast';
import {
	useClaimConversationMutation,
	useReleaseConversationMutation,
	useTransferConversationMutation,
	useListTransferCandidatesQuery,
} from 'api/metaWhatsappApi';
import useCurrentUserId from '../hooks/useCurrentUserId';

/**
 * Who is handling this WhatsApp thread.
 *
 * ★ ONE THREAD, ONE OWNER — AND THE SERVER DECIDES.
 * The customer has a single WhatsApp conversation with the business no matter
 * how many CRM leads they turn into. Everyone with access to any related lead
 * can read it; exactly one person may reply. This bar shows that state and
 * offers the two transitions an agent can make themselves.
 *
 * Nothing here enforces anything. `viewerIsOwner` comes from the server and the
 * send endpoint re-checks ownership on every message, so a stale render can
 * only produce a clear refusal, never a double-reply.
 */

/** A refused ownership change is normal — say which kind. */
const OWNERSHIP_ERROR = {
	ALREADY_OWNED: 'Someone else claimed this first.',
	CONVERSATION_NOT_FOUND: 'This conversation no longer exists.',
	// The compare-and-swap lost: ownership moved between this screen loading and
	// the button being pressed.
	OWNER_CHANGED: 'Ownership changed while you were looking. Nothing was moved.',
	TARGET_CANNOT_VIEW: 'That person cannot open this conversation, so it was not moved.',
	TARGET_NOT_FOUND: 'That person no longer has an account.',
	NOT_CONVERSATION_OWNER: 'You are not handling this conversation.',
	// The server refuses to hand over a thread nobody holds unless the caller
	// is a supervisor; an agent takes it by claiming.
	CONVERSATION_UNCLAIMED: 'Nobody is handling this conversation yet. Claim it first.',
};

/**
 * Hand the conversation to another agent.
 *
 * Candidates are the channel's assignees, computed by the server — the browser
 * has no business deciding who may work a number. They are fetched only when
 * the menu opens, so browsing the inbox does not cost a request per thread.
 */
function TransferMenu({ conversationId, owner, onError }) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	// Being *unstarted* matters as much as being in flight: the query is skipped
	// until the menu opens, so without this the menu renders "nobody to transfer
	// to" before the request has even been made.
	const { data: candidates = [], isFetching, isUninitialized } =
		useListTransferCandidatesQuery(conversationId, { skip: !isOpen });
	const loading = isFetching || isUninitialized;
	const [transfer, { isLoading }] = useTransferConversationMutation();

	const hand = async (toUserId) => {
		try {
			await transfer({
				conversationId,
				toUserId,
				// What this screen believed when the agent decided. If it is stale
				// the server refuses rather than overwriting.
				expectedOwner: owner,
			}).unwrap();
			onClose();
		} catch (err) {
			onError(err);
		}
	};

	return (
		<Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end">
			<MenuButton as={Button} size="xs" variant="outline" isLoading={isLoading}>
				Transfer
			</MenuButton>
			<MenuList minW="14rem" bg="bg.elevated" borderColor="border.default">
				{loading && (
					<MenuItem isDisabled icon={<Spinner size="xs" />}>
						Loading people…
					</MenuItem>
				)}
				{!loading && candidates.length === 0 && (
					<MenuItem isDisabled>
						<Box>
							<Text fontSize="sm">Nobody else to transfer to</Text>
							<Text fontSize="xs" color="text.muted">
								Only people assigned to this number can take it.
							</Text>
						</Box>
					</MenuItem>
				)}
				{/* ★ DISABLED WHILE ONE IS IN FLIGHT. `isLoading` sat on the
				    MenuButton, which is behind the open menu — the items stayed
				    live, and a second click sent a second transfer carrying the
				    `expectedOwner` this screen still believed. The server refused
				    it correctly, so a successful hand-over ended in "Ownership
				    changed while you were looking. Nothing was moved." */}
				{candidates.map((c) => (
					<MenuItem key={c._id} onClick={() => hand(c._id)} fontSize="sm" isDisabled={isLoading}>
						<Avatar size="xs" name={c.name} mr="2" />
						{c.name}
					</MenuItem>
				))}
			</MenuList>
		</Menu>
	);
}

export default function OwnershipBar({ conversation }) {
	const [claim, { isLoading: claiming }] = useClaimConversationMutation();
	const [release, { isLoading: releasing }] = useReleaseConversationMutation();
	const notify = useAppToast();

	const [transferToSelf, { isLoading: taking }] = useTransferConversationMutation();
	const myUserId = useCurrentUserId();

	const {
		_id, owner, ownerUser, viewerIsOwner, relatedLeads = [],
		ownerHoldsNoLead, viewerMayTake,
		// ★ WHAT THIS PERSON MAY DO, ANSWERED BY THE SERVER.
		// These used to be inferred here from `viewerIsOwner` alone, which is one
		// of four ways to be allowed to move a thread — so a Manager holding
		// every supervision permission saw no Transfer and no Release on their
		// own agent's conversation, and reported that hand-over "does not work".
		// The call would have succeeded; the button was never drawn.
		//
		// `undefined` means an older server, so each falls back to the rule that
		// server enforced. Nothing regresses on the way out.
		viewerMayClaim,
		viewerMayTransfer,
		viewerMayRelease,
		viewerMayTakeOver = false,
		viewerIsSupervisor = false,
	} = conversation;

	/**
	 * Take a thread whose holder has lost every lead on this customer.
	 *
	 * ★ ONE CLICK, NO MANAGER. The lead is already yours; the conversation
	 * simply did not follow it, because moving it automatically would drop a
	 * customer mid-sentence. The server re-checks that the holder really has no
	 * claim before it moves anything, records the hand-over, and tells the
	 * person who lost it — so this is visible, not a snatch.
	 */
	const take = async () => {
		try {
			await transferToSelf({ conversationId: _id, toUserId: myUserId, expectedOwner: owner }).unwrap();
		} catch (err) {
			explain(err, 'This conversation could not be taken.');
		}
	};

	const explain = (err, fallback) => {
		const reason = err?.data?.error;
		notify.info('Conversation unchanged', OWNERSHIP_ERROR[reason] ?? fallback);
	};

	const run = async (fn, id, fallback) => {
		try {
			await fn(id).unwrap();
		} catch (err) {
			explain(err, fallback);
		}
	};

	return (
		<Flex
			// Tighter gutters on a phone; every pixel here is a pixel the status
			// sentence and the action buttons are fighting over.
			px={{ base: 2, md: 4 }}
			py={{ base: 1.5, md: 2 }}
			align="center"
			justify="space-between"
			gap="2"
			bg="wa.header"
			borderBottomWidth="1px"
			borderColor="wa.divider"
		>
			<HStack spacing="2" minW="0" overflow="hidden">
				{owner ? (
					<>
						{/* ⚠ THE SENTENCE GIVES WAY, THE CHIPS DO NOT.
						    Flex squeezes whichever child can shrink, and a clipped
						    badge reads as a rendering fault ("2 LEA") while a
						    shortened sentence reads as a sentence. `minW=0` is what
						    lets the text ellipsize inside a flex row at all. */}
						<Avatar size="xs" name={ownerUser?.name} flexShrink={0} />
						<Text fontSize="xs" color="text.primary" noOfLines={1} minW="0">
							{viewerIsOwner ? (
								<>
									You are handling this
								</>
							) : (
								<>
									<strong>{ownerUser?.name ?? 'Another agent'}</strong> is handling this
								</>
							)}
						</Text>
					</>
				) : (
					<Text fontSize="xs" color="text.muted">
						Nobody is handling this conversation
					</Text>
				)}

				{/* ★ THE THREAD AND ITS CRM RECORD HAVE COME APART.
				    Reassigning a lead deliberately does not move the conversation,
				    so the customer is never dropped mid-sentence — but it leaves a
				    thread whose holder has no claim on this customer any more,
				    while the agent who now does cannot reply. Until this said so,
				    the only way anyone found out was a customer going unanswered.
				    The server decides it, by asking whether the holder could still
				    open the thread if they did not own it. */}
				{ownerHoldsNoLead && (
					<Tooltip
						label={`${relatedLeads.map((l) => l.leadName).join(', ')} — now assigned to someone else. Ask a manager to hand the conversation over.`}
						placement="bottom"
						hasArrow
					>
						<Badge colorScheme="orange" variant="subtle" fontSize="0.65rem" flexShrink={0}>
							Lead moved away
						</Badge>
					</Tooltip>
				)}

				{/* Many leads, one conversation — the reason ownership is separate
				    from lead assignment in the first place.
				    ⚠ Hidden while the thread is stranded: this bar has five things
				    in it and a drawer is a few hundred pixels wide, so the two lead
				    chips together pushed the buttons off. The urgent one wins and
				    carries the lead names in its tooltip — the count alone said
				    less than the state does. */}
				{!ownerHoldsNoLead && relatedLeads.length > 1 && (
					<Tooltip
						label={relatedLeads.map((l) => l.leadName).join(', ')}
						placement="bottom"
						hasArrow
					>
						<Badge colorScheme="purple" variant="subtle" fontSize="0.65rem" flexShrink={0}>
							{relatedLeads.length} leads
						</Badge>
					</Tooltip>
				)}
				{!ownerHoldsNoLead && relatedLeads.length === 1 && (
					<Badge colorScheme="gray" variant="subtle" fontSize="0.65rem" flexShrink={0}>
						{relatedLeads[0].leadName}
					</Badge>
				)}
				{relatedLeads.length === 0 && (
					<Badge colorScheme="gray" variant="subtle" fontSize="0.65rem" flexShrink={0}>
						No lead linked
					</Badge>
				)}
			</HStack>

			{/* ⚠ THE ACTIONS NEVER SHRINK, THE STATUS NEVER OVERLAPS THEM.
			    Adding one more badge to the left was enough to push the badges
			    under the Transfer button in a narrow panel — the drawer is only a
			    few hundred pixels wide. Flex would rather squash a button than
			    clip a label, so the actions are pinned and the status side is
			    allowed to run out of room instead. */}
			<HStack spacing="2" flexShrink={0}>
				{/* ★ THE LEAD IS ALREADY YOURS; THIS IS JUST THE KEYBOARD.
				    The conversation stays put when a lead moves so the customer is
				    never dropped mid-sentence — which left the person who now owns
				    the relationship asking a manager for a hand-over nobody would
				    refuse. The server decides `viewerMayTake` and re-checks it
				    before moving anything; the previous holder is told. */}
				{(viewerMayTake || viewerMayTakeOver) && (
					<Button
						size="xs"
						colorScheme="orange"
						isLoading={taking}
						onClick={take}
					>
						{/* Two different situations, two different words. Taking a
						    STRANDED thread is picking up something nobody holds any
						    claim to; taking one from your own agent is an override,
						    and it should read like one. */}
						{viewerMayTakeOver && !viewerMayTake ? 'Take over' : 'Take this chat'}
					</Button>
				)}
				{(viewerMayClaim ?? !owner) && (
					<Button
						size="xs"
						colorScheme="green"
						isLoading={claiming}
						onClick={() => run(claim, _id, 'This conversation could not be claimed.')}
					>
						Claim to reply
					</Button>
				)}
				{/* Its holder, a supervisor above them in the reporting tree, or
				    someone whose scope covers every lead. The server answers which,
				    and re-checks before it moves anything. */}
				{(viewerMayTransfer ?? viewerIsOwner) && (
					<TransferMenu
						conversationId={_id}
						owner={owner}
						onError={(err) => explain(err, 'This conversation could not be transferred.')}
					/>
				)}
				{(viewerMayRelease ?? viewerIsOwner) && (
					<Button
						size="xs"
						variant="ghost"
						isLoading={releasing}
						onClick={() => run(release, _id, 'This conversation could not be released.')}
					>
						{/* Named for what it does to someone ELSE's thread, so a
						    supervisor is not left wondering whose chat they just
						    dropped. */}
						{viewerIsSupervisor && !viewerIsOwner ? 'Unassign' : 'Release'}
					</Button>
				)}
			</HStack>
		</Flex>
	);
}
